import { FilterModel } from '../../filter/domain/filterModel';
import { DateTime } from 'luxon';
import { DATE_FORMAT_MMDDYYYYLUXON } from '../../utils';
import { Severity } from '../../graphql/enums';

export enum ActivityOrderBy {
  MostRecentReported = 'MOST_RECENT_REPORTED',
  MostRecentUpdated = 'MOST_RECENT_UPDATED',
  ReminderDate = 'REMINDER_DATE',
  CreationDate = 'CREATION_DATE',
}

export enum ActivityFilterKey {
  Activity = 'ACTIVITY_FILTER',
  AssetActivity = 'ASSET_ACTIVITY_FILTER',
}

export enum DateTypeFilter {
  ALL = 'all',
  TODAY = 'today',
  THISWEEK = 'thisWeek',
  THISMONTH = 'thisMonth',
  CUSTOMDATE = 'customDate',
}

export enum ActivityOptions {
  EVERYTHING = 'EVERYTHING',
  MENTIONED = 'MENTIONED',
  PINNED = 'PINNED',
  SCHEDULED = 'SCHEDULED',
  NOT_SCHEDULED = 'NOT_SCHEDULED',
}

export class ActivityFilterModel implements FilterModel {
  orderBy?: ActivityOrderBy;
  severity?: string[];
  assetName?: string;
  groupIds?: string[];
  includeAllGroups?: boolean;
  includeUngrouped?: boolean;
  labelIds?: string[];
  includeAllLabels?: boolean;
  includeUnlabeled?: boolean;
  keyword?: string;
  dateType?: DateTypeFilter;
  startDate?: string;
  endDate?: string;
  isCombinedGroupsFilter?: boolean;
  activityOption?: ActivityOptions;
  /**
   * UI-only marker used by the Campaigns toggle to record OFF state.
   * Convention: contains `'CAMPAIGN'` when the toggle is OFF; undefined / empty
   * (or missing `'CAMPAIGN'`) when ON. Not passed to findIssues — the main feed
   * always excludes raw `CAMPAIGN` issue rows via `excludeActivityTypes` (see
   * `getActivityFilter`), since campaign activity surfaces only through the
   * aggregated query.
   */
  activityTypes?: string[];

  constructor(data?: any) {
    // Convert string orderBy back to enum if needed
    if (data?.orderBy && typeof data.orderBy === 'string') {
      // Validate that the string is a valid enum value
      const validOrderByValues = Object.values(ActivityOrderBy);
      if (validOrderByValues.includes(data.orderBy as ActivityOrderBy)) {
        this.orderBy = data.orderBy as ActivityOrderBy;
      } else {
        console.warn('Invalid orderBy value:', data.orderBy, 'falling back to default');
        this.orderBy = ActivityOrderBy.MostRecentUpdated;
      }
    } else {
      this.orderBy = data?.orderBy ?? ActivityOrderBy.MostRecentUpdated;
    }

    this.severity = data?.severity ?? Object.values(Severity);
    this.assetName = data?.assetName;
    this.groupIds = data?.groupIds;
    this.labelIds = data?.labelIds;
    this.activityOption = data?.activityOption ?? ActivityOptions.EVERYTHING;
    this.keyword = data?.keyword;
    this.dateType = data?.dateType ?? DateTypeFilter.ALL;
    this.startDate = data?.startDate ?? '';
    this.endDate = data?.endDate ?? '';
    this.isCombinedGroupsFilter = data?.isCombinedGroupsFilter;
    this.activityOption = data?.activityOption ?? ActivityOptions.EVERYTHING;
    this.activityTypes = data?.activityTypes;

    // Migrate old format: if new pill fields are absent, infer from the legacy arrays.
    // Old groupIds=[] or undefined → both pills on (default = show everything).
    // Old groupIds=['x'] → specific group selected, both meta-pills off.
    if (data?.includeAllGroups !== undefined || data?.includeUngrouped !== undefined) {
      this.includeAllGroups = data.includeAllGroups ?? true;
      this.includeUngrouped = data.includeUngrouped ?? true;
    } else {
      const hasSpecificGroups = data?.groupIds && data.groupIds.length > 0;
      this.includeAllGroups = !hasSpecificGroups;
      this.includeUngrouped = !hasSpecificGroups;
    }

    if (data?.includeAllLabels !== undefined || data?.includeUnlabeled !== undefined) {
      this.includeAllLabels = data.includeAllLabels ?? true;
      this.includeUnlabeled = data.includeUnlabeled ?? true;
    } else {
      const hasSpecificLabels = data?.labelIds && data.labelIds.length > 0;
      this.includeAllLabels = !hasSpecificLabels;
      this.includeUnlabeled = !hasSpecificLabels;
    }
  }

  public toJSON(): any {
    return {
      orderBy: this.orderBy,
      severity: this.severity,
      assetName: this.assetName,
      groupIds: this.groupIds,
      includeAllGroups: this.includeAllGroups,
      includeUngrouped: this.includeUngrouped,
      labelIds: this.labelIds,
      includeAllLabels: this.includeAllLabels,
      includeUnlabeled: this.includeUnlabeled,
      keyword: this.keyword,
      dateType: this.dateType,
      startDate: this.startDate,
      endDate: this.endDate,
      isCombinedGroupsFilter: this.isCombinedGroupsFilter,
      activityOption: this.activityOption,
      activityTypes: this.activityTypes,
    };
  }

  public getFilter(): any {
    const filter: any = {};

    if (this.severity) {
      filter.severity = this.severity;
    }

    // Groups: map pill state → API fields
    const includeAll = this.includeAllGroups ?? true;
    const includeUngrouped = this.includeUngrouped ?? true;
    const specificGroups = (this.groupIds ?? []).filter(Boolean);
    const isDefaultGroups = includeAll && includeUngrouped && specificGroups.length === 0;

    if (!isDefaultGroups) {
      if (includeAll) {
        // "All" pill: all grouped items — explicitly exclude ungrouped
        filter.includeUngrouped = false;
      } else {
        if (specificGroups.length > 0) {
          filter.groupIds = specificGroups;
          if (this.isCombinedGroupsFilter) {
            filter.isCombinedGroupsFilter = this.isCombinedGroupsFilter;
          }
        }
        if (includeUngrouped) {
          filter.includeUngrouped = true;
        }
      }
    } else if (specificGroups.length > 0) {
      // Legacy safety: specific groups with both pills on (shouldn't happen but handle it)
      filter.groupIds = specificGroups;
      if (this.isCombinedGroupsFilter) {
        filter.isCombinedGroupsFilter = this.isCombinedGroupsFilter;
      }
    }

    // Labels: map pill state → API fields
    const includeAllLabels = this.includeAllLabels ?? true;
    const includeUnlabeled = this.includeUnlabeled ?? true;
    const specificLabels = (this.labelIds ?? []).filter(Boolean);
    const isDefaultLabels = includeAllLabels && includeUnlabeled && specificLabels.length === 0;

    if (!isDefaultLabels) {
      if (includeAllLabels) {
        filter.includeUnlabeled = false;
      } else {
        if (specificLabels.length > 0) {
          filter.labelIds = specificLabels;
        }
        if (includeUnlabeled) {
          filter.includeUnlabeled = true;
        }
      }
    } else if (specificLabels.length > 0) {
      filter.labelIds = specificLabels;
    }

    if (this.keyword) {
      filter.keyword = this.keyword;
    }

    if (this.activityOption && this.activityOption !== ActivityOptions.EVERYTHING) {
      filter.activityOption = this.activityOption;
    }

    const dateRange = this.getDateRangeByType();
    if (dateRange?.start && dateRange?.end) {
      filter.startDate = dateRange?.start;
      filter.endDate = dateRange?.end;
    }

    return filter;
  }

  public getTypesCount(): number {
    let count = 0;

    if (this.severity) {
      count += this.severity.length;
    }

    if (this.activityOption && this.activityOption !== ActivityOptions.EVERYTHING) {
      count += 1;
    }

    return count;
  }

  public getGroupsCount(): number {
    const includeAll = this.includeAllGroups ?? true;
    const includeUngrouped = this.includeUngrouped ?? true;
    const specificGroups = (this.groupIds ?? []).filter(Boolean);
    const isDefault = includeAll && includeUngrouped && specificGroups.length === 0;
    if (isDefault) return 0;
    // Count the number of non-default selections
    return specificGroups.length + (includeAll ? 1 : 0) + (includeUngrouped ? 1 : 0);
  }

  public getLabelsCount(): number {
    const includeAllLabels = this.includeAllLabels ?? true;
    const includeUnlabeled = this.includeUnlabeled ?? true;
    const specificLabels = (this.labelIds ?? []).filter(Boolean);
    const isDefault = includeAllLabels && includeUnlabeled && specificLabels.length === 0;
    if (isDefault) return 0;
    return specificLabels.length + (includeAllLabels ? 1 : 0) + (includeUnlabeled ? 1 : 0);
  }

  public scrubWorkspaceData() {
    this.groupIds = undefined;
    this.includeAllGroups = true;
    this.includeUngrouped = true;
    this.isCombinedGroupsFilter = undefined;
  }

  public isDateFilterActive() {
    return this.dateType !== DateTypeFilter.ALL;
  }

  private getDateRangeByType() {
    const now = DateTime.now();

    const dateRanges: Record<
      DateTypeFilter,
      () => { start: DateTime; end: DateTime } | undefined
    > = {
      [DateTypeFilter.TODAY]: () => {
        const start = now.startOf('day').toUTC();
        const end = now.endOf('day').toUTC();
        return { start, end };
      },
      [DateTypeFilter.THISWEEK]: () => {
        const start = now.minus({ days: 6 }).startOf('day').toUTC();
        const end = now.endOf('day').toUTC();
        return { start, end };
      },
      [DateTypeFilter.THISMONTH]: () => {
        const start = now.minus({ days: 29 }).startOf('day').toUTC();
        const end = now.endOf('day').toUTC();
        return { start, end };
      },
      [DateTypeFilter.CUSTOMDATE]: () => {
        if (!this.startDate || !this.endDate) return undefined;

        const start = DateTime.fromFormat(
          this.startDate,
          DATE_FORMAT_MMDDYYYYLUXON
        );
        const end = DateTime.fromFormat(
          this.endDate,
          DATE_FORMAT_MMDDYYYYLUXON
        );

        if (!start.isValid || !end.isValid) {
          console.error('Invalid start or end date');
          return undefined;
        }

        const startUTC = start.startOf('day').toUTC();
        const endUTC = end.endOf('day').toUTC();

        return { start: startUTC, end: endUTC };
      },

      [DateTypeFilter.ALL]: () => undefined,
    };

    return dateRanges[this.dateType ?? DateTypeFilter.ALL]?.();
  }
}
